import { createAction, handleActions } from 'redux-actions';
import http from '../../utility/http';
import { isObjectEmpty, replaceObjectInList } from '../../utility/helpers';
import { parseDuplicateParameters } from '../../utility/uiUtils';
import {
  API_MY_COURSES,
  API_ONE_ON_ONE_ME,
  API_ONE_ON_ONE_USER_REQUEST,
  API_TO_DO,
  API_TEAM_STATS,
  API_ACTION_PLANS_ME,
  API_MY_TASKS,
  api_task_completion,
} from '../../constants/apiRoutes';
import {
  DASHBOARD_DEFAULT_PARAMS,
  MY_COURSES_ORDERING,
} from '../../constants/dashboard';

// ------------------------------------
// Constants
// ------------------------------------

const SET_MY_TO_DO = 'SET_MY_TO_DO';
const SET_TEAM_STATS = 'SET_TEAM_STATS';
const SET_PLANS = 'SET_PLANS';
const LOAD_MORE_PLANS = 'LOAD_MORE_PLANS';
const SET_MY_COURSES = 'SET_MY_COURSES';
const LOAD_MORE_COURSES = 'LOAD_MORE_COURSES';
const SET_ONE_ON_ONE = 'SET_ONE_ON_ONE';
const LOAD_MORE_ONE_ON_ONES = 'LOAD_MORE_ONE_ON_ONES';
const SET_MY_ONE_ON_ONE_REQUEST = 'SET_MY_ONE_ON_ONE_REQUEST';
const SET_MY_TASKS = 'SET_MY_TASKS';
const LOAD_MORE_TASKS = 'LOAD_MORE_TASKS';
const SET_MY_TASK_STATUS = 'SET_MY_TASK_STATUS';
const SET_LOADERS = 'SET_LOADERS';

const initialState = {
  loadings: {
    plansLoader: true,
    myCoursesLoader: true,
    oneOnOneLoader: true,
    myTasksLoader: true,
    myToDoLoader: true,
    teamStatsLoader: true,
  },
  plans: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  myCourses: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  oneOnOne: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  myOnOneRequest: {},
  myTasks: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
  myToDo: [],
  teamStats: [],
};

// ------------------------------------
// Actions
// ------------------------------------

const setMyToDo = createAction(SET_MY_TO_DO);
const setTeamStats = createAction(SET_TEAM_STATS);
const setPlans = createAction(SET_PLANS);
const loadMorePlans = createAction(LOAD_MORE_PLANS);
const setMyCourses = createAction(SET_MY_COURSES);
const loadMoreCourses = createAction(LOAD_MORE_COURSES);
const setOneOnOne = createAction(SET_ONE_ON_ONE);
const loadMoreOneOnOne = createAction(LOAD_MORE_ONE_ON_ONES);
const setMyOneOnOneRequest = createAction(SET_MY_ONE_ON_ONE_REQUEST);
const setMyTasks = createAction(SET_MY_TASKS);
const loadMoreTasks = createAction(LOAD_MORE_TASKS);
const setMyTaskStatus = createAction(SET_MY_TASK_STATUS);
export const setLoaders = createAction(SET_LOADERS);

// ------------------------------------
// Reducers
// ------------------------------------

const reducers = {
  [SET_MY_TO_DO]: (state, { payload }) => ({ ...state, myToDo: payload }),
  [SET_PLANS]: (state, { payload }) => ({ ...state, plans: payload }),
  [LOAD_MORE_PLANS]: (state, { payload }) =>
    isObjectEmpty(payload)
      ? { ...state, plans: payload }
      : {
          ...state,
          plans: {
            ...payload,
            results: state.plans.results
              ? [...state.plans.results, ...payload.results]
              : [...payload.results],
          },
        },
  [SET_MY_COURSES]: (state, { payload }) => ({ ...state, myCourses: payload }),
  [LOAD_MORE_COURSES]: (state, { payload }) =>
    isObjectEmpty(payload)
      ? { ...state, myCourses: payload }
      : {
          ...state,
          myCourses: {
            ...payload,
            results: state.myCourses.results
              ? [...state.myCourses.results, ...payload.results]
              : [...payload.results],
          },
        },
  [SET_ONE_ON_ONE]: (state, { payload }) => ({
    ...state,
    oneOnOne: payload,
  }),
  [LOAD_MORE_ONE_ON_ONES]: (state, { payload }) =>
    isObjectEmpty(payload)
      ? { ...state, oneOnOne: payload }
      : {
          ...state,
          oneOnOne: {
            ...payload,
            results: state.oneOnOne.results
              ? [...state.oneOnOne.results, ...payload.results]
              : [...payload.results],
          },
        },
  [SET_MY_ONE_ON_ONE_REQUEST]: (state, { payload }) => ({
    ...state,
    myOnOneRequest: payload,
  }),
  [SET_MY_TASKS]: (state, { payload }) => ({ ...state, myTasks: payload }),
  [LOAD_MORE_TASKS]: (state, { payload }) =>
    isObjectEmpty(payload)
      ? { ...state, myTasks: payload }
      : {
          ...state,
          myTasks: {
            ...payload,
            results: state.myTasks.results
              ? [...state.myTasks.results, ...payload.results]
              : [...payload.results],
          },
        },
  [SET_MY_TASK_STATUS]: (state, { payload }) => {
    const taskIndex = state.myTasks.results.findIndex(
      currentTask => currentTask.id === payload.id
    );

    return {
      ...state,
      myTasks: {
        ...state.myTasks,
        results: replaceObjectInList(state.myTasks.results, taskIndex, {
          ...state.myTasks.results[taskIndex],
          is_finished: payload.is_finished,
        }),
      },
    };
  },
  [SET_TEAM_STATS]: (state, { payload }) => ({
    ...state,
    teamStats: payload,
  }),
  [SET_LOADERS]: (state, { payload }) => ({
    ...state,
    loadings: { ...state.loadings, ...payload },
  }),
};

// ------------------------------------
// API calls
// ------------------------------------

export const getMyToDo = dispatch =>
  http
    .get(API_TO_DO)
    .then(({ data }) => dispatch(setMyToDo(data)))
    .finally(() => {
      dispatch(setLoaders({ myToDoLoader: false }));
    });

export const getPlans = (
  dispatch,
  url = API_ACTION_PLANS_ME,
  params = {},
  isLoadMore = false
) =>
  http
    .get(url, {
      params: {
        ...DASHBOARD_DEFAULT_PARAMS,
        ...params,
      },
    })
    .then(({ data }) =>
      isLoadMore ? dispatch(loadMorePlans(data)) : dispatch(setPlans(data))
    )
    .finally(() => {
      dispatch(setLoaders({ plansLoader: false }));
    });

export const getMyCourses = (dispatch, params = {}, isLoadMore = false) =>
  http
    .get(API_MY_COURSES, {
      params: {
        ...DASHBOARD_DEFAULT_PARAMS,
        ...MY_COURSES_ORDERING,
        ...params,
      },
    })
    .then(({ data }) =>
      isLoadMore
        ? dispatch(loadMoreCourses(data))
        : dispatch(setMyCourses(data))
    )
    .finally(() => {
      if (!isLoadMore) {
        dispatch(setLoaders({ myCoursesLoader: false }));
      }
    });

export const getOneOnOne = (
  dispatch,
  url = API_ONE_ON_ONE_ME,
  params = {},
  isLoadMore = false
) =>
  http
    .get(url, {
      params: {
        ...DASHBOARD_DEFAULT_PARAMS,
        ...params,
      },
    })
    .then(({ data }) =>
      isLoadMore
        ? dispatch(loadMoreOneOnOne(data))
        : dispatch(setOneOnOne(data))
    );

export const getMyOneOnOneRequest = dispatch =>
  http.get(API_ONE_ON_ONE_USER_REQUEST).then(({ data }) => {
    dispatch(setMyOneOnOneRequest(data));
  });

export const getMyTasks = (dispatch, params = {}, isLoadMore = false) =>
  http
    .get(API_MY_TASKS, {
      params: {
        ...DASHBOARD_DEFAULT_PARAMS,
        ...params,
      },
    })
    .then(({ data }) => {
      if (isLoadMore) {
        dispatch(loadMoreTasks(data));
      } else {
        dispatch(setMyTasks(data));
      }

      return data;
    })
    .finally(() => {
      dispatch(setLoaders({ myTasksLoader: false }));
    });

export const changeTaskStatus = (dispatch, taskId) =>
  http
    .patch(api_task_completion(taskId))
    .then(({ data }) => dispatch(setMyTaskStatus(data)));

export const getTeamStats = (dispatch, users = []) =>
  http
    .get(API_TEAM_STATS, {
      params: {
        id: users,
      },
      paramsSerializer: d => parseDuplicateParameters(d),
    })
    .then(({ data }) => {
      dispatch(setTeamStats(data));

      return data;
    })
    .finally(() => {
      dispatch(setLoaders({ teamStatsLoader: false }));
    });

export default handleActions(reducers, initialState);

// ------------------------------------
// "plain" action functions
// ------------------------------------

export const clearPlans = () => setPlans(initialState.plans);

export const clearMyCourses = () => setMyCourses(initialState.myCourses);

export const clearOneOnOne = () => setOneOnOne(initialState.oneOnOne);

export const clearMyOneOnOneRequest = () =>
  setMyOneOnOneRequest(initialState.myOnOneRequest);

export const clearMyTasks = () => setMyTasks(initialState.myTasks);

export const clearMyToDo = () => setMyToDo(initialState.myToDo);

export const clearTeamStats = () => setTeamStats(initialState.teamStats);

export const clearAllLoaders = () => setLoaders(initialState.loadings);
